import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogContent,
	Box,
	Typography,
	Avatar,
	Grid,
	Paper,
	Stack,
	IconButton,
	Chip,
	Button,
	TextField,
	MenuItem,
	InputAdornment,
	Snackbar,
	Alert,
	Tabs,
	Tab,
	Divider,
	CircularProgress,
	useMediaQuery,
} from '@mui/material';
import {
	Close as CloseIcon,
	Person as PersonIcon,
	Email as EmailIcon,
	Phone as PhoneIcon,
	CalendarToday as CalendarIcon,
	AdminPanelSettings as AdminIcon,
	Hotel as HotelIcon,
	VerifiedUser as VerifiedIcon,
	CardMembership as CardIcon,
	Public as PublicIcon,
	Edit as EditIcon,
	PhotoCamera as PhotoCameraIcon,
	Save as SaveIcon,
	Lock as LockIcon,
	AccountBalance as BankIcon,
	Payments as PaymentsIcon,
	Add as AddIcon,
	Delete as DeleteIcon,
	Star as StarIcon,
	StarBorder as StarBorderIcon,
	Receipt as ReceiptIcon,
	TrendingUp as TrendingUpIcon,
	AssignmentTurnedIn as PayrollIcon,
	CheckCircle as CheckCircleIcon,
	HourglassBottom as HourglassIcon,
	RateReview as RateReviewIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { userService, nationalityService, payrollService, payrollRequestService } from '../services/api';

const AVATAR_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api').replace('/api', '') + '/public';
const getAvatarUrl = (avatar) => {
	if (!avatar) return undefined;
	const p = typeof avatar === 'object' ? avatar.path : avatar;
	return p ? `${ AVATAR_BASE }${ p }` : undefined;
};

const InfoRow = ({ icon, label, value, color = 'primary.main' }) => (
	<Paper variant="outlined" sx={ { p: 2, borderRadius: 2, bgcolor: 'background.paper', height: '100%' } }>
		<Box sx={ { display: 'flex', alignItems: 'center', gap: 1.5 } }>
			<Box sx={ {
				width: 40, height: 40, borderRadius: 2,
				bgcolor: 'action.hover', color,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
			} }>
				{ icon }
			</Box>
			<Box sx={ { minWidth: 0, flex: 1 } }>
				<Typography variant="caption" color="text.secondary">{ label }</Typography>
				<Typography variant="body2" fontWeight={ 500 } noWrap>{ value || '-' }</Typography>
			</Box>
		</Box>
	</Paper>
);

const formatDate = (d) => {
	if (!d) return '';
	try {
		return new Date(d).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
	} catch {
		return '';
	}
};

const formatVND = (n) => {
	const v = Number(n) || 0;
	return v.toLocaleString('vi-VN') + ' ₫';
};

const STATUS_LABELS = {
	pending: 'Chờ xác nhận',
	confirmed: 'Đã xác nhận',
	checked_in: 'Đã nhận phòng',
	completed: 'Hoàn tất',
	cancelled: 'Đã huỷ',
};

const STATUS_COLORS = {
	pending: 'default',
	confirmed: 'info',
	checked_in: 'warning',
	completed: 'success',
	cancelled: 'error',
};

const EMPTY_FORM = { fullName: '', email: '', phone: '', nationality: '', password: '' };

export function IncomeView({ income, loading, month, year, onMonthChange, onYearChange, hidePicker }) {
	const months = Array.from({ length: 12 }, (_, i) => i + 1);
	const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

	// Khi hidePicker: hiển thị pending income (chưa chiết khấu) — reset về 0 sau khi phát lương
	// Khi không hidePicker: hiển thị tổng từ các phiếu lương đã phát trong tháng
	const totals = hidePicker
		? {
			baseSalary: Number(income?.baseSalary) || 0,
			totalCommission: Number(income?.totalCommission) || 0,
			totalAdvance: Number(income?.totalAdvance) || 0,
			netAmount: Number(income?.netIncome) || 0,
			totalBookings: (income?.bookings || []).length,
			totalAdvanceCount: (income?.advances || []).length,
			count: 0,
		}
		: income?.payslipsTotals || {
			baseSalary: 0,
			totalCommission: 0,
			totalAdvance: 0,
			netAmount: 0,
			totalBookings: 0,
			totalAdvanceCount: 0,
			count: 0,
		};

	return (
		<Box>
			{/* Filter — chỉ hiển thị khi không ở chế độ pending */ }
			{ !hidePicker && (
				<Stack direction="row" spacing={ 1.5 } sx={ { mb: 2 } } alignItems="center">
					<TextField
						select size="small" label="Tháng"
						value={ month }
						onChange={ (e) => onMonthChange(Number(e.target.value)) }
						sx={ { minWidth: 110 } }
						SelectProps={ { MenuProps: { disableScrollLock: true, disablePortal: true } } }
					>
						{ months.map((m) => <MenuItem key={ m } value={ m }>Tháng { m }</MenuItem>) }
					</TextField>
					<TextField
						select size="small" label="Năm"
						value={ year }
						onChange={ (e) => onYearChange(Number(e.target.value)) }
						sx={ { minWidth: 110 } }
						SelectProps={ { MenuProps: { disableScrollLock: true, disablePortal: true } } }
					>
						{ years.map((y) => <MenuItem key={ y } value={ y }>{ y }</MenuItem>) }
					</TextField>
					<Chip
						size="small"
						color={ totals.count > 0 ? 'success' : 'default' }
						variant="outlined"
						label={ totals.count > 0 ? `Đã phát ${ totals.count } phiếu` : 'Chưa có phiếu lương' }
					/>
				</Stack>
			) }

			{ loading ? (
				<Box sx={ { display: 'flex', justifyContent: 'center', py: 6 } }>
					<CircularProgress />
				</Box>
			) : !income ? (
				<Typography color="text.secondary" align="center" sx={ { py: 4 } }>Không có dữ liệu</Typography>
			) : (
				<Stack spacing={ 2 }>
					{/* Summary cards — cộng dồn từ tất cả phiếu lương trong tháng */ }
					<Grid container spacing={ 2 }>
						<Grid item xs={ 6 } md={ 3 }>
							<Paper sx={ { p: 2, borderRadius: 2, background: (t) => `linear-gradient(135deg, ${ t.palette.primary.light }33, ${ t.palette.primary.main }1a)`, border: '1px solid', borderColor: 'primary.light' } }>
								<Stack direction="row" spacing={ 1 } alignItems="center" sx={ { mb: 1 } }>
									<PaymentsIcon color="primary" fontSize="small" />
									<Typography variant="caption" color="text.secondary" fontWeight={ 600 }>CƠ BẢN</Typography>
								</Stack>
								<Typography variant="h6" fontWeight={ 700 }>{ formatVND(totals.baseSalary) }</Typography>
							</Paper>
						</Grid>
						<Grid item xs={ 6 } md={ 3 }>
							<Paper sx={ { p: 2, borderRadius: 2, background: (t) => `linear-gradient(135deg, ${ t.palette.secondary.light }33, ${ t.palette.secondary.main }1a)`, border: '1px solid', borderColor: 'secondary.light' } }>
								<Stack direction="row" spacing={ 1 } alignItems="center" sx={ { mb: 1 } }>
									<ReceiptIcon color="secondary" fontSize="small" />
									<Typography variant="caption" color="text.secondary" fontWeight={ 600 }>CHIẾT KHẤU ({ totals.totalBookings } đơn)</Typography>
								</Stack>
								<Typography variant="h6" fontWeight={ 700 }>{ formatVND(totals.totalCommission) }</Typography>
							</Paper>
						</Grid>
						<Grid item xs={ 6 } md={ 3 }>
							<Paper sx={ { p: 2, borderRadius: 2, background: 'linear-gradient(135deg, #ed6c0233, #ed6c021a)', border: '1px solid', borderColor: 'warning.light' } }>
								<Stack direction="row" spacing={ 1 } alignItems="center" sx={ { mb: 1 } }>
									<PaymentsIcon color="warning" fontSize="small" />
									<Typography variant="caption" color="text.secondary" fontWeight={ 600 }>SỐ DƯ ({ totals.totalAdvanceCount } lần)</Typography>
								</Stack>
								<Typography variant="h6" fontWeight={ 700 } color="warning.main">{ formatVND(totals.totalAdvance) }</Typography>
							</Paper>
						</Grid>
						<Grid item xs={ 6 } md={ 3 }>
							<Paper sx={ { p: 2, borderRadius: 2, background: (t) => `linear-gradient(135deg, ${ t.palette.success.light }33, ${ t.palette.success.main }1a)`, border: '1px solid', borderColor: 'success.light' } }>
								<Stack direction="row" spacing={ 1 } alignItems="center" sx={ { mb: 1 } }>
									<TrendingUpIcon color="success" fontSize="small" />
									<Typography variant="caption" color="text.secondary" fontWeight={ 600 }>THỰC NHẬN</Typography>
								</Stack>
								<Typography variant="h6" fontWeight={ 700 } color="success.main">{ formatVND(totals.netAmount) }</Typography>
							</Paper>
						</Grid>
					</Grid>

					{/* Số dư list (pending — chưa được phát phiếu) */ }
					{ (income.advances || []).length > 0 && (
						<Paper variant="outlined" sx={ { borderRadius: 2 } }>
							<Box sx={ { p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 } }>
								<PaymentsIcon fontSize="small" color="warning" />
								<Typography variant="subtitle2" fontWeight={ 700 } sx={ { flex: 1 } }>
									Số dư tháng { income.month }/{ income.year }
								</Typography>
								<Typography variant="caption" color="text.secondary">{ (income.advances || []).length } lần</Typography>
							</Box>
							<Stack divider={ <Divider /> }>
								{ (income.advances || []).map((a) => (
									<Box key={ a._id } sx={ { p: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 } }>
										<Box sx={ { width: 36, height: 36, borderRadius: 1.5, bgcolor: 'warning.50', color: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }>
											<PaymentsIcon fontSize="small" />
										</Box>
										<Box sx={ { flex: 1, minWidth: 0 } }>
											<Typography variant="body2" fontWeight={ 600 } noWrap>{ a.note || 'Số dư' }</Typography>
											<Typography variant="caption" color="text.secondary">{ formatDate(a.date) }</Typography>
										</Box>
										<Typography variant="body2" fontWeight={ 700 } color="warning.main" sx={ { whiteSpace: 'nowrap' } }>{ formatVND(a.amount) }</Typography>
									</Box>
								)) }
							</Stack>
						</Paper>
					) }

					{/* Đơn đặt phòng — chưa chiết khấu (pending) */ }
					<Paper variant="outlined" sx={ { borderRadius: 2 } }>
						<Box sx={ { p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 } }>
							<ReceiptIcon fontSize="small" color="action" />
							<Typography variant="subtitle2" fontWeight={ 700 } sx={ { flex: 1 } }>
								{ hidePicker ? 'Đơn chưa chiết khấu' : `Đơn chưa chiết khấu tháng ${ income.month }/${ income.year }` }
							</Typography>
							<Chip size="small" variant="outlined" label={ `${ (income.bookings || []).length } đơn` } />
						</Box>
						{ (income.bookings || []).length === 0 ? (
							<Box sx={ { py: 3, textAlign: 'center', color: 'text.secondary' } }>
								<Typography variant="body2">Không có đơn chưa chiết khấu</Typography>
							</Box>
						) : (
							<Stack divider={ <Divider /> }>
								{ income.bookings.map((b) => (
									<Box key={ b._id } sx={ { p: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 } }>
										<Box sx={ { width: 36, height: 36, borderRadius: 1.5, bgcolor: 'secondary.50', color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }>
											<ReceiptIcon fontSize="small" />
										</Box>
										<Box sx={ { flex: 1, minWidth: 0 } }>
											<Stack direction="row" spacing={ 1 } alignItems="center" sx={ { mb: 0.25 } }>
												<Typography variant="body2" fontWeight={ 600 } noWrap sx={ { flex: 1, minWidth: 0 } }>
													{ b.hotel?.name || (b.isRandom ? (b.booking_number || 'Đơn') : '—') }
												</Typography>
												<Chip size="small" label={ `${ b.commission_rate }%` } color="info" variant="outlined" sx={ { height: 20, fontSize: '0.7rem' } } />
											</Stack>
											<Stack direction="row" spacing={ 1 } alignItems="center" flexWrap="wrap">
												<Typography variant="caption" color="text.secondary" sx={ { fontFamily: 'monospace' } }>
													{ b.booking_number }
												</Typography>
												<Typography variant="caption" color="text.secondary">·</Typography>
												<Typography variant="caption" color="text.secondary">{ formatDate(b.createdAt) }</Typography>
												<Typography variant="caption" color="text.secondary">·</Typography>
												<Typography variant="caption" color="text.secondary">{ formatVND(b.grand_total) }</Typography>
											</Stack>
										</Box>
										<Box sx={ { textAlign: 'right', flexShrink: 0 } }>
											<Typography variant="body2" fontWeight={ 700 } color="secondary.main" sx={ { whiteSpace: 'nowrap' } }>
												+{ formatVND(b.commission_amount) }
											</Typography>
											<Chip size="small" label={ STATUS_LABELS[b.booking_status] || b.booking_status } color={ STATUS_COLORS[b.booking_status] || 'default' } variant="outlined" sx={ { height: 20, fontSize: '0.7rem', mt: 0.25 } } />
										</Box>
									</Box>
								)) }
							</Stack>
						) }
						{ (income.bookings || []).length > 0 && (
							<>
								<Divider />
								<Box sx={ { p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 3 } }>
									<Box sx={ { textAlign: 'right' } }>
										<Typography variant="caption" color="text.secondary">Tổng doanh thu</Typography>
										<Typography variant="body2" fontWeight={ 600 }>{ formatVND(income.totalRevenue) }</Typography>
									</Box>
									<Box sx={ { textAlign: 'right' } }>
										<Typography variant="caption" color="text.secondary">Tổng CK pending</Typography>
										<Typography variant="body2" fontWeight={ 700 } color="secondary.main">{ formatVND(income.totalCommission) }</Typography>
									</Box>
								</Box>
							</>
						) }
					</Paper>

					{/* Final total — cộng dồn từ tất cả phiếu lương */ }
					<Paper sx={ { p: 2.5, borderRadius: 2, background: (t) => `linear-gradient(135deg, ${ t.palette.primary.main }, ${ t.palette.secondary.main })`, color: 'white' } }>
						<Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } }>
							<Box>
								<Typography variant="caption" sx={ { opacity: 0.9 } }>THỰC NHẬN (lương + chiết khấu − số dư)</Typography>
								<Typography variant="h5" fontWeight={ 800 }>{ formatVND(totals.netAmount) }</Typography>
								<Typography variant="caption" sx={ { opacity: 0.85 } }>
									{ formatVND(totals.baseSalary) } + { formatVND(totals.totalCommission) } − { formatVND(totals.totalAdvance) }
								</Typography>
							</Box>
							<TrendingUpIcon sx={ { fontSize: 48, opacity: 0.6 } } />
						</Box>
					</Paper>
				</Stack>
			) }
		</Box>
	);
}

export default function ProfileModal({ open, onClose }) {
	const { user, refreshUserData } = useAuth();
	const [editing, setEditing] = useState(false);
	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [avatarFile, setAvatarFile] = useState(null);
	const [avatarPreview, setAvatarPreview] = useState('');
	const [nationalities, setNationalities] = useState([]);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

	const [tab, setTab] = useState(0);
	const [bankAccounts, setBankAccounts] = useState([]);
	const [savingBanks, setSavingBanks] = useState(false);
	const isMobile = useMediaQuery('(max-width:899px)');

	const [income, setIncome] = useState(null);
	const [incomeLoading, setIncomeLoading] = useState(false);
	const today = new Date();

	const [payrolls, setPayrolls] = useState([]);
	const [payrollLoading, setPayrollLoading] = useState(false);
	const [payrollYear, setPayrollYear] = useState(today.getFullYear());
	const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
	const [reviewReason, setReviewReason] = useState('');
	const [reviewTargetId, setReviewTargetId] = useState(null);
	const [reviewSubmitting, setReviewSubmitting] = useState(false);

	// Yêu cầu phát lương
	const [payrollRequests, setPayrollRequests] = useState([]);
	const [requestDialogOpen, setRequestDialogOpen] = useState(false);
	const [requestMessage, setRequestMessage] = useState('');
	const [requestSubmitting, setRequestSubmitting] = useState(false);

	const userId = user?._id || user?.id;

	useEffect(() => {
		if (open && user) {
			setForm({
				fullName: user.fullName || user.name || '',
				email: user.email || '',
				phone: user.phone || '',
				nationality: user.nationality || '',
				password: '',
			});
			setEditing(false);
			setAvatarFile(null);
			setAvatarPreview('');
			setErrors({});
			setBankAccounts(Array.isArray(user.bankAccounts) ? user.bankAccounts.map((b) => ({ ...b })) : []);
			setTab(0);
		}
	}, [open, user]);

	useEffect(() => {
		if (open && tab === 1) {
			setIncomeLoading(true);
			userService.getMyIncome()
				.then((res) => {
					if (res?.success) setIncome(res.data);
				})
				.catch(() => setIncome(null))
				.finally(() => setIncomeLoading(false));
		}
	}, [open, tab]);

	useEffect(() => {
		if (open && tab === 2) {
			setPayrollLoading(true);
			Promise.all([
				payrollService.listMine({ year: payrollYear }),
				payrollRequestService.listMine().catch(() => ({ data: [] })),
			])
				.then(([pRes, rRes]) => {
					setPayrolls(pRes?.data || []);
					setPayrollRequests(rRes?.data || []);
				})
				.catch(() => { setPayrolls([]); setPayrollRequests([]); })
				.finally(() => setPayrollLoading(false));
		}
	}, [open, tab, payrollYear]);

	const reloadPayrollRequests = async () => {
		try {
			const r = await payrollRequestService.listMine();
			setPayrollRequests(r?.data || []);
		} catch (_) { }
	};

	const handleSubmitPayrollRequest = async () => {
		setRequestSubmitting(true);
		try {
			await payrollRequestService.create({ message: requestMessage.trim() });
			showMsg('Đã gửi yêu cầu phát lương đến admin');
			setRequestDialogOpen(false);
			setRequestMessage('');
			await reloadPayrollRequests();
		} catch (e) {
			showMsg(e?.response?.data?.message || 'Lỗi gửi yêu cầu', 'error');
		} finally {
			setRequestSubmitting(false);
		}
	};

	const handleCancelPayrollRequest = async (id) => {
		try {
			await payrollRequestService.cancel(id);
			showMsg('Đã huỷ yêu cầu');
			await reloadPayrollRequests();
		} catch (e) {
			showMsg(e?.response?.data?.message || 'Lỗi huỷ yêu cầu', 'error');
		}
	};

	const handleConfirmPayroll = async (id) => {
		try {
			await payrollService.confirm(id);
			setPayrolls((prev) => prev.map((p) => p._id === id ? { ...p, status: 'confirmed', confirmedAt: new Date() } : p));
			showMsg('Đã xác nhận phiếu lương');
		} catch (e) {
			showMsg(e?.response?.data?.message || 'Lỗi xác nhận', 'error');
		}
	};

	const openReviewDialog = (id) => {
		setReviewTargetId(id);
		setReviewReason('');
		setReviewDialogOpen(true);
	};

	const handleSubmitReview = async () => {
		if (!reviewReason.trim()) { showMsg('Vui lòng nhập lý do', 'warning'); return; }
		setReviewSubmitting(true);
		try {
			await payrollService.requestReview(reviewTargetId, reviewReason.trim());
			setPayrolls((prev) => prev.map((p) => p._id === reviewTargetId ? { ...p, status: 'review_requested', reviewReason: reviewReason.trim() } : p));
			showMsg('Đã gửi yêu cầu xem xét');
			setReviewDialogOpen(false);
		} catch (e) {
			showMsg(e?.response?.data?.message || 'Lỗi gửi yêu cầu', 'error');
		} finally {
			setReviewSubmitting(false);
		}
	};

	useEffect(() => {
		if (editing && nationalities.length === 0 && nationalityService?.getAll) {
			nationalityService.getAll({ limit: 300 })
				.then((res) => setNationalities(res?.data || []))
				.catch(() => { });
		}
	}, [editing, nationalities.length]);

	if (!user) return null;

	const showMsg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

	const fullName = form.fullName || user.fullName || user.name || user.username || '';
	const initial = (fullName || '?').charAt(0).toUpperCase();
	const isAdmin = user.role === 'admin' || user.isAdmin;
	const isActive = user.isActive !== false && user.status !== 'blocked';

	const handleChange = (field) => (e) => {
		const v = e.target.value;
		setForm((p) => ({ ...p, [field]: v }));
		if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
	};

	const handlePickAvatar = (e) => {
		const f = e.target.files?.[0];
		if (!f) return;
		if (avatarPreview) URL.revokeObjectURL(avatarPreview);
		setAvatarFile(f);
		setAvatarPreview(URL.createObjectURL(f));
		e.target.value = '';
	};

	const validate = () => {
		const err = {};
		if (!form.fullName.trim()) err.fullName = 'Bắt buộc';
		if (!form.email.trim()) err.email = 'Bắt buộc';
		else if (!/\S+@\S+\.\S+/.test(form.email)) err.email = 'Email không hợp lệ';
		if (form.password && form.password.length < 6) err.password = 'Tối thiểu 6 ký tự';
		return err;
	};

	const handleSave = async () => {
		const err = validate();
		if (Object.keys(err).length) { setErrors(err); return; }
		if (!userId) { showMsg('Không xác định được người dùng', 'error'); return; }
		setSaving(true);
		try {
			const payload = {
				fullName: form.fullName,
				email: form.email,
				phone: form.phone,
				nationality: form.nationality,
			};
			if (form.password) payload.password = form.password;
			if (avatarFile) {
				try { await userService.uploadAvatar(userId, avatarFile); } catch (_) { /* non-blocking */ }
			}
			await userService.update(userId, payload);
			await refreshUserData?.();
			showMsg('Cập nhật hồ sơ thành công');
			setEditing(false);
			if (avatarPreview) URL.revokeObjectURL(avatarPreview);
			setAvatarFile(null);
			setAvatarPreview('');
			setForm((p) => ({ ...p, password: '' }));
		} catch (e) {
			showMsg(e?.response?.data?.message || 'Lỗi cập nhật', 'error');
		} finally {
			setSaving(false);
		}
	};

	const handleCancelEdit = () => {
		setEditing(false);
		setErrors({});
		if (avatarPreview) URL.revokeObjectURL(avatarPreview);
		setAvatarFile(null);
		setAvatarPreview('');
		setForm({
			fullName: user.fullName || user.name || '',
			email: user.email || '',
			phone: user.phone || '',
			nationality: user.nationality || '',
			password: '',
		});
	};

	// ── Bank account helpers ────────────────────────────────
	const addBankAccount = () => {
		setBankAccounts((prev) => [
			...prev,
			{ bankName: '', accountNumber: '', isDefault: prev.length === 0 },
		]);
	};
	const updateBankAccount = (idx, field, value) => {
		setBankAccounts((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
	};
	const removeBankAccount = (idx) => {
		setBankAccounts((prev) => {
			const next = prev.filter((_, i) => i !== idx);
			if (next.length && !next.some((b) => b.isDefault)) next[0].isDefault = true;
			return next;
		});
	};
	const setDefaultBankAccount = (idx) => {
		setBankAccounts((prev) => prev.map((b, i) => ({ ...b, isDefault: i === idx })));
	};
	const handleSaveBanks = async () => {
		if (!userId) return;
		// Validate
		for (const b of bankAccounts) {
			if (!b.bankName?.trim() || !b.accountNumber?.trim()) {
				showMsg('Vui lòng điền đầy đủ tên ngân hàng và số tài khoản', 'error');
				return;
			}
		}
		setSavingBanks(true);
		try {
			await userService.update(userId, { bankAccounts });
			await refreshUserData?.();
			showMsg('Cập nhật tài khoản ngân hàng thành công');
		} catch (e) {
			showMsg(e?.response?.data?.message || 'Lỗi cập nhật', 'error');
		} finally {
			setSavingBanks(false);
		}
	};

	return (
		<Dialog open={ open } onClose={ onClose } maxWidth="md" fullWidth disableScrollLock
			fullScreen={ isMobile }
			PaperProps={ { sx: { borderRadius: { xs: 0, md: 3 }, overflow: 'hidden' } } }
		>
			{/* Hero header */ }
			<Box sx={ {
				position: 'relative',
				background: (theme) => `linear-gradient(135deg, ${ theme.palette.primary.main } 0%, ${ theme.palette.secondary.main } 100%)`,
				color: 'white',
				px: { xs: 2, md: 4 }, pt: { xs: 2, md: 4 }, pb: { xs: 5, md: 7 },
			} }>
				<IconButton
					onClick={ onClose }
					size="small"
					sx={ { position: 'absolute', right: 12, top: 12, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } } }
				>
					<CloseIcon fontSize="small" />
				</IconButton>
				<Typography variant="h5" fontWeight={ 700 } sx={ { fontSize: { xs: 18, md: 24 } } }>Hồ sơ của tôi</Typography>
				<Typography variant="body2" sx={ { opacity: 0.9, mt: 0.5, fontSize: { xs: 12, md: 14 } } }>
					{ editing ? 'Chỉnh sửa thông tin cá nhân' : 'Thông tin chi tiết tài khoản' }
				</Typography>
			</Box>

			<Box sx={ { display: 'flex', justifyContent: 'center', mt: { xs: -4, md: -6 }, mb: 1 } }>
				<Box sx={ { position: 'relative' } }>
					<Avatar
						src={ avatarPreview || getAvatarUrl(user.avatar) }
						sx={ { width: { xs: 80, md: 110 }, height: { xs: 80, md: 110 }, bgcolor: 'primary.main', fontSize: { xs: 32, md: 40 }, border: '4px solid', borderColor: 'background.paper', boxShadow: 3, cursor: editing ? 'pointer' : 'default' } }
						onClick={ () => editing && document.getElementById('profile-modal-avatar-input')?.click() }
					>
						{ initial }
					</Avatar>
					{ editing && (
						<>
							<IconButton
								size="small"
								onClick={ () => document.getElementById('profile-modal-avatar-input')?.click() }
								sx={ { position: 'absolute', bottom: 0, right: 0, bgcolor: 'primary.main', color: 'white', border: '2px solid', borderColor: 'background.paper', '&:hover': { bgcolor: 'primary.dark' } } }
							>
								<PhotoCameraIcon sx={ { fontSize: 18 } } />
							</IconButton>
							<input
								id="profile-modal-avatar-input"
								type="file"
								accept="image/*"
								hidden
								onChange={ handlePickAvatar }
							/>
						</>
					) }
				</Box>
			</Box>

			<Box sx={ { textAlign: 'center', px: 3, mb: 2 } }>
				<Typography variant="h6" fontWeight={ 700 }>{ fullName }</Typography>
				<Typography variant="body2" color="text.secondary">{ user.email }</Typography>
				<Stack direction="row" spacing={ 1 } justifyContent="center" sx={ { mt: 1.5 } }>
					<Chip
						icon={ <AdminIcon sx={ { fontSize: 16 } } /> }
						label={ isAdmin ? 'Admin' : 'User' }
						size="small"
						color={ isAdmin ? 'secondary' : 'default' }
						variant={ isAdmin ? 'filled' : 'outlined' }
					/>
					<Chip
						icon={ <VerifiedIcon sx={ { fontSize: 16 } } /> }
						label={ isActive ? 'Đang hoạt động' : 'Đã khoá' }
						size="small"
						color={ isActive ? 'success' : 'error' }
						variant="outlined"
					/>
				</Stack>
				{ avatarPreview && (
					<Typography variant="caption" color="primary" sx={ { display: 'block', mt: 1 } }>
						Ảnh chưa lưu – bấm "Lưu thay đổi" để cập nhật
					</Typography>
				) }
			</Box>

			<Box sx={ { borderBottom: 1, borderColor: 'divider', px: { xs: 1, md: 4 } } }>
				<Tabs
					value={ tab }
					onChange={ (_, v) => { setTab(v); if (v !== 0) setEditing(false); } }
					variant={ isMobile ? 'scrollable' : 'fullWidth' }
					scrollButtons={ false }
					sx={ { '& .MuiTab-root': { minHeight: { xs: 40, md: 48 }, fontSize: { xs: 12, md: 14 }, minWidth: { xs: 'auto', md: 0 }, px: { xs: 1.25, md: 2 } } } }
				>
					<Tab icon={ <PersonIcon fontSize="small" /> } iconPosition="start" label="Thông tin chung" />
					<Tab icon={ <TrendingUpIcon fontSize="small" /> } iconPosition="start" label="Thu nhập" />
					<Tab icon={ <PayrollIcon fontSize="small" /> } iconPosition="start" label="Phiếu lương" />
				</Tabs>
			</Box>

			<DialogContent sx={ { px: { xs: 1.75, md: 4 }, pb: { xs: 2, md: 3 }, pt: 2, bgcolor: 'background.default' } }>
				{ tab === 0 && (
					<>
						{ !editing ? (
							<>
								<Grid container spacing={ 2 }>
									<Grid item xs={ 12 } sm={ 6 }>
										<InfoRow icon={ <PersonIcon fontSize="small" /> } label="Họ và tên" value={ fullName } />
									</Grid>
									<Grid item xs={ 12 } sm={ 6 }>
										<InfoRow icon={ <EmailIcon fontSize="small" /> } label="Email" value={ user.email } />
									</Grid>
									{ user.phone && (
										<Grid item xs={ 12 } sm={ 6 }>
											<InfoRow icon={ <PhoneIcon fontSize="small" /> } label="Số điện thoại" value={ user.phone } />
										</Grid>
									) }
									{ user.nationality && (
										<Grid item xs={ 12 } sm={ 6 }>
											<InfoRow icon={ <PublicIcon fontSize="small" /> } label="Quốc tịch" value={ user.nationality } />
										</Grid>
									) }
									<Grid item xs={ 12 } sm={ 6 }>
										<InfoRow icon={ <HotelIcon fontSize="small" /> } label="Giới hạn đặt phòng" value={ `${ user.limitRoom ?? 3 } phòng` } />
									</Grid>
									{ user.userCode && (
										<Grid item xs={ 12 } sm={ 6 }>
											<InfoRow icon={ <CardIcon fontSize="small" /> } label="Mã giới thiệu" value={ user.userCode } />
										</Grid>
									) }
									{ (user.createdAt || user.created_at) && (
										<Grid item xs={ 12 } sm={ 6 }>
											<InfoRow icon={ <CalendarIcon fontSize="small" /> } label="Ngày tham gia" value={ formatDate(user.createdAt || user.created_at) } />
										</Grid>
									) }
								</Grid>
								<Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 } }>
									<Button variant="outlined" onClick={ onClose }>Đóng</Button>
									<Button variant="contained" startIcon={ <EditIcon /> } onClick={ () => setEditing(true) }>
										Chỉnh sửa hồ sơ
									</Button>
								</Box>
							</>
						) : (
							<>
								<Paper variant="outlined" sx={ { p: 2.5, borderRadius: 2, bgcolor: 'background.paper', mb: 2 } }>
									<Box sx={ { display: 'flex', alignItems: 'center', gap: 1, mb: 2 } }>
										<PersonIcon color="primary" fontSize="small" />
										<Typography variant="subtitle2" fontWeight={ 700 }>Thông tin cá nhân</Typography>
									</Box>
									<Grid container spacing={ 2 }>
										<Grid item xs={ 12 } sm={ 6 }>
											<TextField
												label="Họ và tên *"
												value={ form.fullName } onChange={ handleChange('fullName') }
												error={ !!errors.fullName } helperText={ errors.fullName }
												fullWidth size="small"
											/>
										</Grid>
										<Grid item xs={ 12 } sm={ 6 }>
											<TextField
												label="Email *" type="email"
												value={ form.email } onChange={ handleChange('email') }
												error={ !!errors.email } helperText={ errors.email }
												fullWidth size="small"
												InputProps={ { startAdornment: <InputAdornment position="start"><EmailIcon sx={ { fontSize: 18, color: 'text.secondary' } } /></InputAdornment> } }
											/>
										</Grid>
										<Grid item xs={ 12 } sm={ 6 }>
											<TextField
												label="Số điện thoại"
												value={ form.phone } onChange={ handleChange('phone') }
												fullWidth size="small"
												InputProps={ { startAdornment: <InputAdornment position="start"><PhoneIcon sx={ { fontSize: 18, color: 'text.secondary' } } /></InputAdornment> } }
											/>
										</Grid>
										<Grid item xs={ 12 } sm={ 6 }>
											<TextField
												select
												label="Quốc tịch"
												value={ form.nationality } onChange={ handleChange('nationality') }
												fullWidth size="small"
												SelectProps={ { MenuProps: { disableScrollLock: true, disablePortal: true } } }
												InputProps={ { startAdornment: <InputAdornment position="start"><PublicIcon sx={ { fontSize: 18, color: 'text.secondary' } } /></InputAdornment> } }
											>
												<MenuItem value=""><em>-- Chọn --</em></MenuItem>
												{ nationalities.map((n) => (
													<MenuItem key={ n._id } value={ n.name }>{ n.name }</MenuItem>
												)) }
											</TextField>
										</Grid>
									</Grid>
								</Paper>

								<Paper variant="outlined" sx={ { p: 2.5, borderRadius: 2, bgcolor: 'background.paper' } }>
									<Box sx={ { display: 'flex', alignItems: 'center', gap: 1, mb: 2 } }>
										<LockIcon color="primary" fontSize="small" />
										<Typography variant="subtitle2" fontWeight={ 700 }>Đổi mật khẩu</Typography>
									</Box>
									<TextField
										label="Mật khẩu mới" type="password"
										value={ form.password } onChange={ handleChange('password') }
										error={ !!errors.password }
										helperText={ errors.password || 'Để trống nếu không muốn đổi' }
										fullWidth size="small"
									/>
								</Paper>

								<Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 } }>
									<Button variant="outlined" onClick={ handleCancelEdit } disabled={ saving }>Huỷ</Button>
									<Button variant="contained" startIcon={ <SaveIcon /> } onClick={ handleSave } disabled={ saving }>
										{ saving ? 'Đang lưu...' : 'Lưu thay đổi' }
									</Button>
								</Box>
							</>
						) }

						{/* Bank accounts */ }
						<Paper variant="outlined" sx={ { p: 2.5, borderRadius: 2, bgcolor: 'background.paper', mt: 2 } }>
							<Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 } }>
								<Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
									<BankIcon color="primary" fontSize="small" />
									<Typography variant="subtitle2" fontWeight={ 700 }>Tài khoản ngân hàng</Typography>
								</Box>
								<Button size="small" startIcon={ <AddIcon /> } onClick={ addBankAccount } variant="outlined">
									Thêm tài khoản
								</Button>
							</Box>

							{ bankAccounts.length === 0 ? (
								<Box sx={ { p: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2, color: 'text.secondary' } }>
									<Typography variant="caption">Chưa có tài khoản nào. Bấm "Thêm tài khoản" để bổ sung.</Typography>
								</Box>
							) : (
								<Stack spacing={ 1.5 }>
									{ bankAccounts.map((b, idx) => (
										<Paper key={ idx } variant="outlined" sx={ { p: 2, borderRadius: 2, borderColor: b.isDefault ? 'primary.main' : 'divider' } }>
											<Grid container spacing={ 1.5 }>
												<Grid item xs={ 12 } sm={ 6 }>
													<TextField
														label="Tên ngân hàng *"
														value={ b.bankName }
														onChange={ (e) => updateBankAccount(idx, 'bankName', e.target.value) }
														fullWidth size="small"
														placeholder="VD: Vietcombank"
													/>
												</Grid>
												<Grid item xs={ 12 } sm={ 6 }>
													<TextField
														label="Số tài khoản *"
														value={ b.accountNumber }
														onChange={ (e) => updateBankAccount(idx, 'accountNumber', e.target.value) }
														fullWidth size="small"
													/>
												</Grid>
											</Grid>
											<Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 } }>
												<Button
													size="small"
													startIcon={ b.isDefault ? <StarIcon sx={ { color: 'warning.main' } } /> : <StarBorderIcon /> }
													onClick={ () => setDefaultBankAccount(idx) }
													sx={ { textTransform: 'none' } }
												>
													{ b.isDefault ? 'Mặc định' : 'Đặt làm mặc định' }
												</Button>
												<Button size="small" color="error" startIcon={ <DeleteIcon /> } onClick={ () => removeBankAccount(idx) } sx={ { textTransform: 'none' } }>
													Xoá
												</Button>
											</Box>
										</Paper>
									)) }
								</Stack>
							) }
							<Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 2 } }>
								<Button variant="contained" startIcon={ <SaveIcon /> } onClick={ handleSaveBanks } disabled={ savingBanks }>
									{ savingBanks ? 'Đang lưu...' : 'Lưu tài khoản ngân hàng' }
								</Button>
							</Box>
						</Paper>
					</>
				) }

				{ tab === 1 && (
					<IncomeView
						income={ income }
						loading={ incomeLoading }
						hidePicker
					/>
				) }

				{ tab === 2 && (
					<Box>
						<Stack direction={ { xs: 'column', sm: 'row' } } spacing={ 1.5 } sx={ { mb: 2 } } alignItems={ { xs: 'stretch', sm: 'center' } } justifyContent="space-between">
							<TextField
								select size="small" label="Năm"
								value={ payrollYear }
								onChange={ (e) => setPayrollYear(Number(e.target.value)) }
								sx={ { minWidth: 110 } }
								SelectProps={ { MenuProps: { disableScrollLock: true, disablePortal: true } } }
							>
								{ Array.from({ length: 5 }, (_, i) => today.getFullYear() - i).map((y) => (
									<MenuItem key={ y } value={ y }>{ y }</MenuItem>
								)) }
							</TextField>
							{ !isAdmin && (
								<Button
									variant="contained"
									color="primary"
									startIcon={ <PaymentsIcon /> }
									onClick={ () => { setRequestMessage(''); setRequestDialogOpen(true); } }
									disabled={ payrollRequests.some((r) => r.status === 'pending') }
								>
									{ payrollRequests.some((r) => r.status === 'pending')
										? 'Đang chờ admin xử lý...'
										: 'Yêu cầu phát lương' }
								</Button>
							) }
						</Stack>

						{/* Danh sách yêu cầu phát lương */ }
						{ payrollRequests.length > 0 && (
							<Paper variant="outlined" sx={ { p: 2, borderRadius: 2, mb: 2 } }>
								<Typography variant="subtitle2" fontWeight={ 700 } sx={ { mb: 1.5 } }>
									Yêu cầu phát lương của tôi
								</Typography>
								<Stack spacing={ 1 }>
									{ payrollRequests.slice(0, 5).map((r) => {
										const meta = {
											pending: { label: 'Đang chờ', color: 'warning' },
											approved: { label: 'Đã phát lương', color: 'success' },
											rejected: { label: 'Bị từ chối', color: 'error' },
										}[r.status] || { label: r.status, color: 'default' };
										return (
											<Box key={ r._id } sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' } }>
												<Box sx={ { minWidth: 0, flex: 1 } }>
													<Stack direction="row" spacing={ 1 } alignItems="center">
														<Chip size="small" label={ meta.label } color={ meta.color } variant="outlined" />
														<Typography variant="caption" color="text.secondary">
															{ formatDate(r.createdAt) }
														</Typography>
													</Stack>
													{ r.message && (
														<Typography variant="caption" color="text.secondary" sx={ { display: 'block', mt: 0.5 } }>Lời nhắn: { r.message }</Typography>
													) }
													{ r.status === 'rejected' && r.rejectReason && (
														<Typography variant="caption" color="error.main" sx={ { display: 'block', mt: 0.5 } }>Lý do từ chối: { r.rejectReason }</Typography>
													) }
												</Box>
												{ r.status === 'pending' && (
													<Button size="small" color="error" onClick={ () => handleCancelPayrollRequest(r._id) }>Huỷ</Button>
												) }
											</Box>
										);
									}) }
								</Stack>
							</Paper>
						) }

						{ payrollLoading ? (
							<Box sx={ { display: 'flex', justifyContent: 'center', py: 6 } }><CircularProgress /></Box>
						) : payrolls.length === 0 ? (
							<Box sx={ { textAlign: 'center', py: 6 } }>
								<PayrollIcon sx={ { fontSize: 48, color: 'grey.400' } } />
								<Typography color="text.secondary" sx={ { mt: 1 } }>Chưa có phiếu lương nào</Typography>
							</Box>
						) : (
							<Stack spacing={ 2 }>
								{ payrolls.map((p) => {
									const statusMeta = {
										issue: { label: 'Chờ xác nhận', color: 'warning', icon: <HourglassIcon fontSize="small" /> },
										issued: { label: 'Chờ xác nhận', color: 'warning', icon: <HourglassIcon fontSize="small" /> },
										confirmed: { label: 'Đã xác nhận', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
										review_requested: { label: 'Đang xem xét', color: 'info', icon: <RateReviewIcon fontSize="small" /> },
										superseded: { label: 'Đã thay thế', color: 'default', icon: null },
									}[p.status] || { label: p.status, color: 'default', icon: null };
									return (
										<Paper key={ p._id } variant="outlined" sx={ { p: 2.5, borderRadius: 2, opacity: p.status === 'superseded' ? 0.55 : 1, borderColor: p.status === 'confirmed' || p.status === 'issued' ? 'success.main' : 'divider' } }>
											<Stack direction={ { xs: 'column', sm: 'row' } } justifyContent="space-between" alignItems={ { xs: 'flex-start', sm: 'center' } } spacing={ 1 }>
												<Box>
													<Typography variant="subtitle1" fontWeight={ 700 }>Phiếu lương ngày { formatDate(p.issuedAt || p.createdAt) }</Typography>
													<Typography variant="caption" color="text.secondary">
														Tháng { p.month }/{ p.year }{ p.issuedBy ? ` · bởi ${ p.issuedBy.fullName || p.issuedBy.username || '' }` : '' }
													</Typography>
												</Box>
												<Chip size="small" label={ statusMeta.label } color={ statusMeta.color } icon={ statusMeta.icon } variant="outlined" />
											</Stack>
											<Divider sx={ { my: 1.5 } } />
											<Grid container spacing={ 2 }>
												<Grid item xs={ 6 } sm={ 3 }>
													<Typography variant="caption" color="text.secondary" display="block">Lương cơ bản</Typography>
													<Typography variant="body2" fontWeight={ 600 }>{ formatVND(p.baseSalary) }</Typography>
												</Grid>
												<Grid item xs={ 6 } sm={ 3 }>
													<Typography variant="caption" color="text.secondary" display="block">Chiết khấu ({ p.totalBookings } đơn)</Typography>
													<Typography variant="body2" fontWeight={ 600 } color="secondary.main">{ formatVND(p.totalCommission) }</Typography>
												</Grid>
												<Grid item xs={ 6 } sm={ 3 }>
													<Typography variant="caption" color="text.secondary" display="block">Số dư ứng ({ p.totalAdvanceCount } lần)</Typography>
													<Typography variant="body2" fontWeight={ 600 } color="warning.main">{ formatVND(p.totalAdvance) }</Typography>
												</Grid>
												<Grid item xs={ 6 } sm={ 3 }>
													<Typography variant="caption" color="text.secondary" display="block">Thực nhận</Typography>
													<Typography variant="body2" fontWeight={ 700 } color="success.main">{ formatVND(p.netAmount) }</Typography>
												</Grid>
											</Grid>
											{ p.note && (
												<Typography variant="caption" color="text.secondary" sx={ { mt: 1, display: 'block' } }>Ghi chú: { p.note }</Typography>
											) }
											{ p.status === 'issued' && (
												<Stack direction="row" spacing={ 1 } sx={ { mt: 2 } }>
													<Button size="small" variant="contained" color="success" startIcon={ <CheckCircleIcon /> } onClick={ () => handleConfirmPayroll(p._id) }>
														Xác nhận
													</Button>
													<Button size="small" variant="outlined" color="warning" startIcon={ <RateReviewIcon /> } onClick={ () => openReviewDialog(p._id) }>
														Yêu cầu xem xét
													</Button>
												</Stack>
											) }
											{ p.status === 'review_requested' && p.reviewReason && (
												<Typography variant="caption" color="info.main" sx={ { mt: 1, display: 'block' } }>Lý do xem xét: { p.reviewReason }</Typography>
											) }
										</Paper>
									);
								}) }
							</Stack>
						) }

						{/* Review dialog */ }
						<Dialog open={ reviewDialogOpen } onClose={ () => setReviewDialogOpen(false) } maxWidth="xs" fullWidth disableScrollLock>
							<DialogContent sx={ { pt: 3 } }>
								<Typography variant="subtitle1" fontWeight={ 700 } sx={ { mb: 1.5 } }>Yêu cầu xem xét lại phiếu lương</Typography>
								<TextField
									label="Lý do *" multiline rows={ 3 } fullWidth
									value={ reviewReason } onChange={ (e) => setReviewReason(e.target.value) }
									placeholder="Mô tả vấn đề hoặc lý do bạn muốn xem xét lại..."
								/>
								<Stack direction="row" spacing={ 1 } justifyContent="flex-end" sx={ { mt: 2 } }>
									<Button onClick={ () => setReviewDialogOpen(false) } disabled={ reviewSubmitting }>Huỷ</Button>
									<Button variant="contained" color="warning" onClick={ handleSubmitReview } disabled={ reviewSubmitting }>
										{ reviewSubmitting ? 'Đang gửi...' : 'Gửi yêu cầu' }
									</Button>
								</Stack>
							</DialogContent>
						</Dialog>

						{/* Request payroll dialog */ }
						<Dialog open={ requestDialogOpen } onClose={ () => !requestSubmitting && setRequestDialogOpen(false) } maxWidth="xs" fullWidth disableScrollLock>
							<DialogContent sx={ { pt: 3 } }>
								<Typography variant="subtitle1" fontWeight={ 700 } sx={ { mb: 0.5 } }>Yêu cầu phát lương</Typography>
								<Typography variant="caption" color="text.secondary" sx={ { display: 'block', mb: 2 } }>
									Yêu cầu sẽ được gửi tới admin. Khi admin phát lương, các đơn hoàn thành và chưa được chiết khấu sẽ được cộng vào phiếu lương của bạn.
								</Typography>
								<TextField
									label="Lời nhắn (tuỳ chọn)" multiline rows={ 3 } fullWidth
									value={ requestMessage } onChange={ (e) => setRequestMessage(e.target.value) }
									placeholder="Ghi chú cho admin..."
								/>
								<Stack direction="row" spacing={ 1 } justifyContent="flex-end" sx={ { mt: 2 } }>
									<Button onClick={ () => setRequestDialogOpen(false) } disabled={ requestSubmitting }>Huỷ</Button>
									<Button variant="contained" onClick={ handleSubmitPayrollRequest } disabled={ requestSubmitting }>
										{ requestSubmitting ? 'Đang gửi...' : 'Gửi yêu cầu' }
									</Button>
								</Stack>
							</DialogContent>
						</Dialog>
					</Box>
				) }
			</DialogContent>

			<Snackbar
				open={ snackbar.open }
				autoHideDuration={ 3000 }
				onClose={ () => setSnackbar({ ...snackbar, open: false }) }
				anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
			>
				<Alert severity={ snackbar.severity } onClose={ () => setSnackbar({ ...snackbar, open: false }) } sx={ { width: '100%' } }>
					{ snackbar.message }
				</Alert>
			</Snackbar>
		</Dialog>
	);
}
